import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Github, FlaskConical, FileText } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const githubProjects = [
  {
    num: "01",
    title: "Echo Truth",
    desc: "Machine-learning fake news detector that scrutinizes news content and verifies its authenticity, trained on a comprehensive Kaggle dataset.",
    tags: ["Python", "Machine Learning", "NLP"],
    caseStudy: "echo-truth",
    link: { label: "GitHub", href: "https://github.com/Sarronnn/EchoTruth", icon: Github },
  },
  {
    num: "02",
    title: "Bioinformatics Research: GRNsight",
    desc: "Open-source gene-network visualization app. Debugged front-end behavior and traced PostgreSQL-backed data so biological relationships render accurately.",
    tags: ["JavaScript", "PostgreSQL", "Open Source"],
    link: { label: "GitHub", href: "https://github.com/dondi/GRNsight", icon: Github },
  },
  {
    num: "03",
    title: "Data Compression Algorithm",
    desc: "Compression algorithms implemented from scratch: encoding, decoding, and the efficiency trade-offs explored hands-on.",
    tags: ["Algorithms", "Compression", "CS Core"],
    link: { label: "GitHub", href: "https://github.com/Sarronnn/Algorithms_Compression", icon: Github },
  },
  {
    num: "04",
    title: "Kis: A New Coding Language",
    desc: "A statically-typed programming language with a module system, operator overloading, and its own keyword set. Language design from parsing to compilation.",
    tags: ["Compilers", "Language Design"],
    caseStudy: "kis",
    link: { label: "GitHub", href: "https://github.com/jmendo15/Kis", icon: Github },
  },
];

const sideProjects = [
  {
    num: "05",
    title: "Mini ChatGPT Project",
    desc: "A compact conversational AI built in a notebook: prompt handling, response generation, and an interactive chat workflow from scratch.",
    tags: ["Python", "NLP", "LLMs"],
    link: { label: "Colab", href: "https://colab.research.google.com/drive/1AeAjjjUjtK_uf0pHPvlb51DyYZakIFFo", icon: FlaskConical },
  },
  {
    num: "06",
    title: "Employee Attrition Project",
    desc: "Predicting employee turnover through exploratory analysis, feature engineering, and classification modeling on HR data to surface retention risks.",
    tags: ["Python", "Classification", "Pandas"],
    link: { label: "Colab", href: "https://drive.google.com/file/d/1y5ZTzKW4W04M4z19TFQldgyB8HP55qIK/view?usp=sharing", icon: FlaskConical },
  },
  {
    num: "07",
    title: "Inferential Statistics",
    desc: "Hypothesis testing, confidence intervals, and statistical inference applied to real datasets. A full notebook study in drawing defensible conclusions.",
    tags: ["Statistics", "Python", "SciPy"],
    link: { label: "Colab", href: "https://colab.research.google.com/drive/1Ooj9f91wM8h1eP1jZKlNs1zCck9JkKZG?usp=sharing", icon: FlaskConical },
  },
  {
    num: "08",
    title: "Movie Recommendation System",
    desc: "A recommender that scores similarity across movie data and serves up what to watch next. Built end-to-end in a notebook.",
    tags: ["Python", "Recommender", "Pandas"],
    link: { label: "Colab", href: "https://drive.google.com/file/d/1X7NWbwriKTyRnA_2kD-tdQAzlzVsRB3l/view?usp=sharing", icon: FlaskConical },
  },
  {
    num: "09",
    title: "Geographic Clustering Project",
    desc: "Unsupervised clustering of geographic data, grouping locations by pattern and visualizing the segments that emerge.",
    tags: ["Clustering", "Scikit-learn", "Geo"],
    link: { label: "Colab", href: "https://colab.research.google.com/drive/1x4NgZXDKrZy1OLWsC4Cm1LJiws-Zw4ie?usp=sharing", icon: FlaskConical },
  },
  {
    num: "10",
    title: "Digit Recognition Project",
    desc: "End-to-end classification on handwritten digits, from feature engineering and model training to evaluation, built during MIT's industry-mentored program.",
    tags: ["Scikit-learn", "Classification"],
    link: { label: "Colab", href: "https://colab.research.google.com/drive/1ogmOTwv4EMJRintzjT4HWAKO35hlmwJ0?usp=sharing", icon: FlaskConical },
  },
  {
    num: "11",
    title: "Market Analysis Project",
    desc: "Market data exploration covering trends, segmentation, and decision-ready insights packaged in a notebook study.",
    tags: ["Python", "EDA", "Visualization"],
    link: { label: "Colab", href: "https://colab.research.google.com/drive/1wflZx3iUeDrwKZm1qhle6Ki3w4GOMbMG?usp=sharing", icon: FlaskConical },
  },
  {
    num: "12",
    title: "Sales Forecasting: ARIMA",
    desc: "Explored trend and seasonality in historical sales data, then built and evaluated an ARIMA model against actuals, reaching 92% forecasting accuracy.",
    tags: ["Python", "ARIMA", "Pandas", "Matplotlib"],
    link: { label: "Colab", href: "https://colab.research.google.com/drive/1FvwvQ9VdA0rVkGQEf2bR9W3HSYet0dut?usp=sharing", icon: FlaskConical },
  },
  {
    num: "13",
    title: "LMU Datathon, 3rd Place",
    desc: "Led a four-person team through a 24-hour competition analyzing EPA contract data in R and Tableau, surfacing opportunities for underrepresented businesses.",
    tags: ["R", "Tableau", "Award"],
    link: null,
  },
  {
    num: "14",
    title: "Marketing & Growth Analytics: CTTG Nonprofit",
    desc: "Volunteer analytics work: dug into Google Analytics and Google Ads campaign data to understand how people discover the nonprofit, found tracking gaps, and sharpened campaign measurement.",
    tags: ["Google Analytics", "Google Ads", "Volunteer"],
    link: null,
  },
];

export default function Projects() {
  return (
    <section id="projects" data-testid="projects-section" className="py-28 md:py-40 border-t border-treeline bg-pine/40">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="font-mono text-xs uppercase tracking-[0.35em] text-lime mb-6"
        >
          [ 03 ] · Side projects &amp; school work
        </motion.p>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-mono text-sm text-sage mb-16 max-w-xl"
        >
          Project links included for viewing (GitHub or Google Colab).
        </motion.p>

        {[
          { label: "Open source & GitHub", items: githubProjects },
          { label: "Side projects + school work", items: sideProjects },
        ].map((group) => (
          <div key={group.label} className="mb-24 last:mb-0">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="font-mono text-[10px] uppercase tracking-[0.3em] text-sage mb-6 border-b border-treeline pb-4"
            >
              {group.label}
            </motion.p>
            <div>
              {group.items.map((p, i) => {
                const Tag = p.caseStudy ? motion(Link) : p.link ? motion.a : motion.div;
                return (
            <Tag
              key={p.num}
              {...(p.caseStudy ? { to: `/case-study/${p.caseStudy}` } : p.link ? { href: p.link.href, target: "_blank", rel: "noopener noreferrer" } : {})}
              data-testid={`project-link-${p.num}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: 0.04 * i, ease: EASE }}
              className={`group relative block border-t border-treeline last:border-b py-10 md:py-12 px-2 md:px-6 overflow-hidden${p.link ? "" : " cursor-default"}`}
            >
              <div className="absolute inset-0 bg-lime translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
              <div className="relative z-10 grid grid-cols-12 gap-6 items-start">
                <span className="col-span-2 md:col-span-1 font-mono text-sm text-lime group-hover:text-forest transition-colors duration-300 pt-1">
                  [{p.num}]
                </span>
                <div className="col-span-10 md:col-span-5">
                  <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight text-cream group-hover:text-forest transition-colors duration-300 flex items-center gap-3">
                    {p.title}
                    {(p.link || p.caseStudy) && (
                    <ArrowUpRight
                      size={26}
                      className="opacity-0 -translate-x-2 translate-y-2 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-all duration-300 text-lime group-hover:text-forest"
                    />
                    )}
                  </h3>
                </div>
                <p className="col-span-12 md:col-span-4 font-mono text-xs md:text-sm leading-relaxed text-sage group-hover:text-forest/80 transition-colors duration-300">
                  {p.desc}
                </p>
                <div className="col-span-12 md:col-span-2 flex md:flex-col md:items-end gap-2 flex-wrap">
                  {(p.link || p.caseStudy) && (
                  <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] border border-lime/50 text-lime group-hover:border-forest group-hover:text-forest px-3 py-1.5 transition-colors duration-300">
                    {p.caseStudy ? (<><FileText size={12} /> Case Study</>) : (<><p.link.icon size={12} /> {p.link.label}</>)}
                  </span>
                  )}
                  {p.tags.map((t) => (
                    <span key={t} className="font-mono text-[10px] uppercase tracking-[0.15em] text-sage group-hover:text-forest/70 transition-colors duration-300">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </Tag>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
